/**
 * Diff palabra-a-palabra entre dos versiones del MISMO texto. Primitivas puras,
 * sin React ni CSS: dado "lo que dijiste" y "lo que deberías decir", devuelve
 * QUÉ tokens cambiaron de cada lado.
 *
 * Nació en `screens/orbitas/target-words.ts` (resolver la palabra foco de una
 * card) y se mudó acá cuando la misma pregunta apareció en las correcciones:
 * Órbitas y las cinco superficies que muestran un par `dijiste → mejor así`
 * (charla viva, debrief destacado, acordeón, foco del debrief, sheet del Radar)
 * comparten UNA sola implementación del diff. Duplicarlo habría significado dos
 * heurísticos derivando a distinto ritmo sobre la misma decisión visual.
 */

/** Un tramo consecutivo de tokens, marcado o no. `end` es exclusivo. */
export interface TokenRun {
  readonly start: number;
  readonly end: number;
  readonly target: boolean;
}

/** Corta un texto en tokens visibles (mismo criterio que el alignment: espacios). */
export function tokenizeText(text: string): string[] {
  return text.split(/\s+/).filter((t) => t.length > 0);
}

/**
 * Clave de comparación de un token: minúsculas sin la puntuación de los bordes.
 * NO es `normalizeWord` (el del tap-to-lookup) a propósito: ese tira los dígitos,
 * y acá "25" tiene que seguir siendo "25" — si no, en "I have 25 years" ↔ "I am
 * 25 years" los dos números quedan en blanco, no matchean entre sí y el diff
 * marca de más. Un token de pura puntuación sí queda vacío (y no matchea nada).
 */
export function compareKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/^[^0-9a-zà-ÿ]+/i, '')
    .replace(/[^0-9a-zà-ÿ']+$/i, '');
}

/**
 * Diff palabra-a-palabra por LCS. Devuelve qué índices de `a` y de `b` NO están
 * en la subsecuencia común, o sea: lo que cambió. Las oraciones de una card o de
 * una corrección son de ~5-20 palabras, así que la DP cuadrática es gratis.
 *
 * Espera los tokens ya pasados por `compareKey` (si no, "That" y "that" cuentan
 * como cambio).
 */
export function diffIndices(
  a: readonly string[],
  b: readonly string[],
): { aOnly: Set<number>; bOnly: Set<number> } {
  const n = a.length;
  const m = b.length;
  // lcs[i][j] = largo de la subsecuencia común de a[i..] y b[j..].
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const row = lcs[i];
      const next = lcs[i + 1];
      if (!row || !next) continue;
      // Un token vacío (puntuación suelta) nunca "matchea" — si no, dos signos
      // distintos se anclarían entre sí y el diff marcaría palabras de más.
      row[j] =
        a[i] !== '' && a[i] === b[j]
          ? (next[j + 1] ?? 0) + 1
          : Math.max(next[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  const aOnly = new Set<number>();
  const bOnly = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] !== '' && a[i] === b[j]) {
      i++;
      j++;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      aOnly.add(i);
      i++;
    } else {
      bOnly.add(j);
      j++;
    }
  }
  for (; i < n; i++) aOnly.add(i);
  for (; j < m; j++) bOnly.add(j);
  return { aOnly, bOnly };
}

/**
 * Saca del resultado del diff lo que no es una palabra (un guion largo, un punto
 * y coma sueltos). El LCS los deja SIEMPRE marcados como cambio a propósito —dos
 * signos distintos no tienen por qué anclarse entre sí—, pero resaltar un "—" no
 * señala nada, y contarlo como edición le sube el precio a un par mínimo que
 * apenas se separa por la puntuación de al lado.
 */
export function dropNonWords(indices: ReadonlySet<number>, keys: readonly string[]): Set<number> {
  const words = new Set<number>();
  for (const i of indices) if (keys[i]) words.add(i);
  return words;
}

/**
 * Agrupa `length` tokens en tramos consecutivos según estén marcados o no.
 *
 * Existe por el render: una expresión de varias palabras ("How's it going?") se
 * resalta como UN bloque, no como tres cajitas sueltas con huecos en los
 * espacios. El que dibuja envuelve cada tramo marcado en un solo elemento, con
 * los espacios internos adentro.
 */
export function groupRuns(length: number, isTarget: (i: number) => boolean): TokenRun[] {
  const runs: TokenRun[] = [];
  let i = 0;
  while (i < length) {
    const target = isTarget(i);
    let j = i + 1;
    while (j < length && isTarget(j) === target) j++;
    runs.push({ start: i, end: j, target });
    i = j;
  }
  return runs;
}
